import Hls, { type HlsConfig } from 'hls.js';

/**
 * hls.js tuning for live ingest.
 *
 * A live stream keeps producing data again after short encoder/muxer hiccups
 * (MediaMTX recreates its HLS muxer), but hls.js defaults give up after a
 * couple of retries, surface a fatal `levelLoadError` and leave the player
 * dead — which the UI renders as "Stream unavailable". These settings retry
 * long enough for the stream to come back on its own.
 */
export const HLS_LIVE_CONFIG: Partial<HlsConfig> = {
  manifestLoadingMaxRetry: 8,
  manifestLoadingRetryDelay: 1000,
  manifestLoadingMaxRetryTimeout: 8000,
  levelLoadingMaxRetry: 10,
  levelLoadingRetryDelay: 1000,
  levelLoadingMaxRetryTimeout: 8000,
  fragLoadingMaxRetry: 10,
  fragLoadingRetryDelay: 1000,
  fragLoadingMaxRetryTimeout: 8000,
};

export type LiveHlsStatus = 'connecting' | 'playing' | 'reconnecting' | 'error';

export interface LiveHlsHandle {
  destroy(): void;
}

const MAX_PLAYER_RESTARTS = 5;
const MAX_MEDIA_RECOVERIES = 3;
const RESTART_DELAY_MS = 2000;

/**
 * Attaches a self-healing HLS player to `video`.
 *
 * - network/manifest/fragment errors: the player is rebuilt after a short delay
 *   (up to MAX_PLAYER_RESTARTS attempts in a row),
 * - media errors: recovered in place (up to MAX_MEDIA_RECOVERIES attempts),
 * - a parsed manifest resets the attempt counters, so a stream that recovers
 *   keeps playing and later hiccups restart the cycle,
 * - `onStatus('error', …)` is only reported once every attempt failed.
 *
 * Callers must invoke `handle.destroy()` on unmount.
 */
export function attachLiveHls(
  video: HTMLVideoElement,
  url: string,
  onStatus: (status: LiveHlsStatus, detail?: string) => void,
): LiveHlsHandle {
  let hls: Hls | null = null;
  let retryTimer: number | null = null;
  let attempts = 0;

  if (!Hls.isSupported()) {
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = url;
      onStatus('playing');
    } else {
      onStatus('error', 'HLS is not supported in this browser');
    }
    return {
      destroy: () => {
        video.removeAttribute('src');
        video.load();
      },
    };
  }

  const start = () => {
    hls?.destroy();
    hls = new Hls(HLS_LIVE_CONFIG);
    onStatus('connecting');

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      attempts = 0;
      onStatus('playing');
    });

    hls.on(Hls.Events.ERROR, (_evt, data) => {
      if (!data.fatal) return;

      if (data.type === Hls.ErrorTypes.MEDIA_ERROR && attempts < MAX_MEDIA_RECOVERIES) {
        attempts += 1;
        hls?.recoverMediaError();
        return;
      }

      if (attempts < MAX_PLAYER_RESTARTS) {
        attempts += 1;
        onStatus('reconnecting', data.details);
        retryTimer = window.setTimeout(start, RESTART_DELAY_MS);
        return;
      }

      onStatus('error', data.details);
    });

    hls.loadSource(url);
    hls.attachMedia(video);
  };

  start();

  return {
    destroy: () => {
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      hls?.destroy();
      hls = null;
    },
  };
}
